import debounce from 'lodash-es/debounce'
import startCase from 'lodash-es/startCase'
import dynamic from 'next/dynamic'
import Error from 'next/error'
import { withRouter } from 'next/router'
import React from 'react'
import type { StreetViewProps } from 'react-google-map-street-view'
import 'lazysizes'
import 'intersection-observer'
import smoothScroll from 'smoothscroll-polyfill'
// import SvgCompare from '#/assets/images/icons/compare_add.svg'
// import AuthLock from '#/components/AuthLock'
import BackFab from '#/components/BackFab'
import ClientOnly from '#/components/ClientOnly'
import OverlaySpinner from '#/components/OverlaySpinner'
import LoadingSpinner from '#/components/OverlaySpinner/Spinners/Hexagon'
import { googleMapKey } from '#/configs'
import { BREAK_LG, BREAK_SM, BREAK_XL, WIDE_VIEWPORT_WIDTH } from '#/consts'
// import { NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL } from '#/consts/neighborhood_level'
import { APP_DETAIL_PAGE, PROPERTY_DETAIL_PAGE } from '#/consts/url'
import BannerAd from '#/containers/Home/components/BannerAd'
import bannerStyles from '#/containers/Home/components/styles.module.scss'
import { logEvent } from '#/services/analytics'
// import { get as getProperty } from '#/services/properties'
// import { getMedia } from '#/services/property'
import { get as getProperty } from '#/services/v4/property/client'
// import { GetHistory, GetEstimateHistory, GetMarketChanges, GetComparables } from '#/store/actions/property'
import { GetHistory, GetMarketChanges, GetPublicRecord } from '#/store/actions/property'
import propertiesSaga from '#/store/sagas/properties'
import propertySaga from '#/store/sagas/property'
import userSaga from '#/store/sagas/user'
import { injectSaga } from '#/store/setup'
import cn from '#/utils/classnames'
// import { isValidNumber } from '#/utils/is_valid_number'
// import { getNeighborhood, viewUrl } from '#/utils/properties'
import { viewUrl } from '#/utils/properties'
import { description } from '#/utils/property'
// import AbsorptionRate from './components/AbsorptionRate/Detail'
import Actions from './components/Actions'
import Agents from './components/Agents'
import BottomBar from './components/BottomBar'
import Breadcrumb from './components/Breadcrumb'
// import Comparables from './components/Comparables'
import DetailView from './components/DetailView'
import Environment from './components/Environment'
import Facts from './components/Facts'
import Header from './components/Header'
// import HistoryChart from './components/HistoryChart'
import HistoryTimeline from './components/HistoryTimeline'
import HistoryTimelineSubView from './components/HistoryTimeline/SubView'
import Images from './components/Images'
import Insights from './components/Insights/Section'
import LocationInformation from './components/LocationInformation'
// import LotDetails from './components/LotDetails'
import MLS from './components/MLS'
import MLSDescription from './components/MLSDescription'
import MLSDetailsSubView from './components/MLSDetails/SubView'
import MarketCondition from './components/MarketCondition'
import NavBar from './components/NavBar'
// import NearbyStatistics from './components/NearbyStatistics'
import Neighborhood from './components/Neighborhood'
import OpenHouses from './components/OpenHouses'
import OverviewInfo from './components/OverviewInfo'
import StickyOverview from './components/OverviewInfo/sticky'
import OwnerInformation from './components/OwnerInformation'
import Permits from './components/Permits'
import PermitHistory from './components/Permits/PermitHistory'
import PropertyDetails from './components/PropertyDetails'
import PublicRecordsSubView from './components/PublicRecords/SubView'
// import RentalValuationChart from './components/RentalValuationChart'
import SchoolInformation from './components/SchoolInformation'
import ShowLocationButton from './components/ShowLocationButton'
import Sitemap from './components/Sitemap'
import TaxDetail from './components/TaxInformation/Detail'
import Tours from './components/Tours'
// import ValuationChart from './components/ValuationChart'
import Valuations from './components/Valuations'
import { Section } from './components/common'
import { metaData } from './meta'
import styles from './styles.module.scss'
import { Props, State, SubViewType } from './types'

if (typeof window !== 'undefined') {
  smoothScroll.polyfill()
}

const StreetView = dynamic<StreetViewProps>(
  () => import('react-google-map-street-view').then(mod => mod.StreetView),
  { ssr: false }
)

class PropertyDetail extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = { mode: 'Page' }

  static fetchSource(id: string) {
    return getProperty(id)
  }

  isAdditionalDataFetched?: boolean

  constructor(props: Props) {
    super(props)

    injectSaga('user', userSaga)
    injectSaga('properties', propertiesSaga)
    injectSaga('property', propertySaga)

    this.state = { subView: { visible: false } }

    if (props.source) {
      this.updateSource(props.source.property, props.source.media)
    }
  }

  async componentDidMount() {
    if (!this.props.source) {
      const {
        id,
        data: { property }
      } = this.props
      if (property?.id !== id) {
        const res = await PropertyDetail.fetchSource(id)
        this.updateSource(res, res.media)
      }
    }

    if (this.props.currentRole !== 'expert') {
      const { query } = this.props.router
      if (query.u && query.s) {
        this.props.getShareLink({
          type: 'Property',
          uid: query.u as string,
          entityId: query.s as string,
          branded: query.br === 'true'
        })
      }
    }

    if (this.props.mode === 'Page' || this.props.viewportWidth <= BREAK_SM) {
      document.addEventListener('scroll', this.fetchAdditionalData)
    } else if (this.props.containerId) {
      const container = document.getElementById(this.props.containerId)
      container?.addEventListener('scroll', this.fetchAdditionalData)
    }
  }

  async componentDidUpdate(prev: Props) {
    if (prev.id !== this.props.id) {
      if (this.props.source) {
        this.updateSource(this.props.source.property, this.props.source.media)
      } else {
        const id = this.props.id as string
        const res = await PropertyDetail.fetchSource(id)
        this.updateSource(res, res.media)
      }

      // reset shared role
      this.props.clearShareRole()
    }
  }

  componentWillUnmount() {
    if (this.props.mode === 'Page') {
      document.removeEventListener('scroll', this.fetchAdditionalData)
    } else if (this.props.containerId) {
      const container = document.getElementById(this.props.containerId)
      container?.removeEventListener('scroll', this.fetchAdditionalData)
    }

    // reset shared role
    this.props.clearShareRole()

    this.props.setIsReferrerGoogle(false)
  }

  // normalize url if needed
  normalizeUrl = (property: TopHap.Property) => {
    if (typeof window === 'undefined') return

    const { router } = this.props
    const normalizedUrl = viewUrl(property.id, property.address)
    // router.asPath  could have hash
    const paths = router.asPath.split('#')

    if (
      router.pathname === PROPERTY_DETAIL_PAGE &&
      paths[0] !== normalizedUrl
    ) {
      router.replace(normalizedUrl + (paths[1] ? `#${paths[1]}` : ''), undefined, { shallow: true })
    }
  }

  updateSource = (
    property: TopHap.ExtendedProperty,
    media?: TopHap.PropertyMedia
  ) => {
    const { data, isReferrerGoogle, fromList, mode } = this.props
    const reuse = data.property?.id === property.id

    if (!reuse) {
      this.props.setPropertyDetails({ property, media })
      this.props.addRecentView({ id: this.props.id })
      this.isAdditionalDataFetched = false
    }

    this.props.setIsReferrerGoogle(isReferrerGoogle)

    this.normalizeUrl(property)

    if (typeof window !== 'undefined') {
      const address = `${property.displayAddress}, ${property.displayRegion}`
      logEvent('view_item', {
        property_id: property.id,
        property_address: address,
        property_status: property.TophapStatus,
        property_url: location.origin + viewUrl(property.id, property.address),
        value: property.estimates?.estimate,
        currency: 'USD',
        source: mode === 'Component' ? (fromList ? 'list' : 'map') : undefined,
        listhub_key: property.isListHub ? property.ListingKey : undefined,
        items: [
          {
            item_id: property.id,
            item_name: address,
            price: property.estimates?.estimate
          }
        ]
      })
    }
  }

  fetchAdditionalData = debounce(() => {
    if (this.isAdditionalDataFetched) return
    this.isAdditionalDataFetched = true

    this.props.getHistory()
    // this.props.getEstimateHistory({ dateOption: [-5, 'y'] })
    // this.props.getRentEstimateHistory({ dateOption: [-5, 'y'] })
    // this.props.getMarketHistory([-2, 'y'])

    this.props.getPublicRecord()

    if (this.props.data.property?.location) {
      const { data: { property } } = this.props

      //   const range = getNeighborhood(
      //     property.zones,
      //     NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL
      //   )?.id || property.zones[0]?.id

      //   if (range) this.props.getNearbyStatistics({ filters: { zones: [range] } })
      //   this.props.getNearbyStatistics({ zoneId: '94598' })

      // const marketZone = property.zones.find(e => e.type === 'zip')?.id || range
      const marketZoneName = property.zones.find(e => e.type === 'zip')?.name
      if (marketZoneName) this.props.getMarketChanges({ zones: [marketZoneName] })

    //   this.props.getPrediction()
    //   this.props.getSchoolDetails()
    //   this.props.getComparables()
    }
  }, 100)

  private get pageMeta() {
    let { data: { property } } = this.props
    const { data: { insightsData } } = this.props
    if (property) {
      return metaData(this.props.mode, insightsData, property)
    } else if (this.props.source) {
      // CONSIDER ME: we are getting property in setPropertyDetails too.
      property = this.props.source.property
      return metaData(this.props.mode, insightsData, property)
    }

    return null
  }

  closeSubView = () => {
    this.setState(prevState => ({
      subView: {
        ...prevState.subView,
        visible: false
      }
    }))
  }

  showSubView = (
    type: SubViewType,
    zone?: string,
    sectionTitle?: string,
    defaultMetric?: TopHap.MarketHistoryMetric | keyof TopHap.MarketHistory
  ) => {
    const { data: { property } } = this.props

    if (!property) return null
    this.setState({ subView: { visible: true, type, defaultMetric, zone } })
    // if (type === 'Valuation') {
    //   this.props.getEstimateHistory({ dateOption: [-5, 'y'] })
    // } else if (type === 'RentalValuation') {
    //   this.props.getRentEstimateHistory({ dateOption: [-5, 'y'] })
    // }
    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'click_card',
      value: type,
      card_title: sectionTitle,
      property_id: property.id,
      address: property.displayAddress
    })
  }

  // get MarketConditionChart() {
  //   const {
  //     asyncStatus,
  //     data: { property, marketHistory }
  //   } = this.props

  //   const { subView: { defaultMetric } } = this.state

  //   if (!property) return null

  //   const estimateDate = property.estimates?.date

  //   return (
  //     <HistoryChart
  //       comparables={
  //         marketHistory ?
  //           marketHistory.items.map(e => ({
  //             ...e,
  //             estimate: []
  //           })) :
  //           []
  //       }
  //       defaultDateOption={marketHistory?.dateOption}
  //       defaultMetric={defaultMetric}
  //       estimateDate={estimateDate}
  //       event='market_condition'
  //       loading={asyncStatus[GetEstimateHistory.type] === 'request'}
  //       type='Market'
  //       updateData={(_, dateOption) =>
  //         this.props.getMarketHistory({
  //           dateOption,
  //           update: true
  //         })}
  //     />
  //   )
  // }

  // get ValuationChart() {
  //   const { data: { property } } = this.props
  //   if (!property) return null

  //   return (
  //     <ValuationChart
  //       property={property}
  //       updateAccuracyData={dateOption =>
  //         this.props.getEstimateHistory({
  //           dateOption,
  //           update: true,
  //           mode: 'accuracy_only'
  //         })}
  //       updateData={(_, dateOption, hasAccuracy) =>
  //         this.props.getEstimateHistory({
  //           dateOption,
  //           update: true,
  //           mode: hasAccuracy ? 'all' : 'without_accuracy'
  //         })}
  //     />
  //   )
  // }

  // get RentalValuationChart() {
  //   const { data: { property } } = this.props
  //   if (!property) return null

  //   return (
  //     <RentalValuationChart
  //       property={property}
  //       updateAccuracyData={dateOption =>
  //         this.props.getRentEstimateHistory({
  //           dateOption,
  //           update: true,
  //           mode: 'accuracy_only'
  //         })}
  //       updateData={(_, dateOption, hasAccuracy) =>
  //         this.props.getRentEstimateHistory({
  //           dateOption,
  //           update: true,
  //           mode: hasAccuracy ? 'all' : 'without_accuracy'
  //         })}
  //     />
  //   )
  // }

  get renderSubView() {
    // const {
    //   authenticated,
    //   data: { property, history, marketChanges },
    //   viewportWidth
    // } = this.props
    const {
      authenticated,
      data: { property, history, publicRecord },
      viewportWidth
    } = this.props
    const { subView } = this.state

    if (!property) return null

    let title = ''
    if (subView.type === 'AbsorptionRate' || subView.type === 'MarketTrends') {
      title =
        subView.type.replace(/([A-Z])/g, ' $1') +
        ' for ' +
        property.zones.filter(zone => zone.id === subView.zone)[0]?.name
    } else {
      title = subView.type ?
        subView.type.replace(/([A-Z])/g, ' $1') +
        ' for ' +
        property.displayAddress :
        ''
    }

    return (
      <DetailView
        isMobile={viewportWidth < BREAK_SM}
        noPadding={
          subView.type === 'StreetView' || subView.type === 'TaxInformation'
        }
        subViewType={subView.type}
        title={title}
        visible={subView.visible}
        whiteBackground={subView.type === 'PermitHistory'}
        onClose={this.closeSubView}
      >
        {subView.type === 'PublicRecords' && publicRecord && (
          <PublicRecordsSubView
            authenticated={authenticated}
            publicRecord={publicRecord}
          />
        )}
        {subView.type === 'PropertyDetails' && history && (
          <MLSDetailsSubView
            history={history}
          />
        )}
        {/* {subView.type === 'MarketTrends' &&
          history &&
          this.MarketConditionChart} */}
        {subView.type === 'HistoryTimeline' && history && (
          <HistoryTimelineSubView history={history} />
        )}
        {subView.type === 'StreetView' && (
          <StreetView
            APIkey={googleMapKey}
            address={property.address.FullAddress}
            mapStyle={{
              width: '100%',
              height: '100%',
              minHeight: '500px'
            }}
            streetView
            zoomLevel={15}
          />
        )}
        {/* {subView.type === 'Valuation' && this.ValuationChart} */}
        {/* {subView.type === 'RentalValuation' && this.RentalValuationChart} */}
        {subView.type === 'PermitHistory' && (
          <PermitHistory history={history} isLocked={false} property={property} />
        )}
        {/* {subView.type === 'AbsorptionRate' && (
          <AbsorptionRate marketChanges={marketChanges?.month} />
        )} */}
        {subView.type === 'TaxInformation' && (
          <TaxDetail history={history} property={property} />
        )}
        {[
          'PublicRecords',
          'PropertyDetails',
          'HistoryTimeline',
          'PermitHistory'
        ].includes(subView.type || '') &&
          !history && <LoadingSpinner className='mb-4 mt-2' size={48} />}
      </DetailView>
    )
  }

  render() {
    if (this.props.errorCode) {
      return <Error statusCode={this.props.errorCode} />
    }

    const {
      asyncStatus,
      authenticated,
      containerId,
      currentRole,
      id,
      isGuest,
      agentInfo,
      // mls,
      mode,
      data,
      isReferrerGoogle,
      source,
      viewportWidth
    } = this.props

    const property = source?.property || data.property
    const media = source?.media || data.media

    if (property?.id !== id) {
      return (
        <div className='min-h-screen'>
          {this.pageMeta}

          <OverlaySpinner absolute bgInvisible text='' visible />
        </div>
      )
    }

    const {
      history,
      estimateHistory,
      rentEstimateHistory,
      marketChanges,
      // predictionBase,
      insightsData,
      // schools,
      // comparables
      ownerInfo,
      publicRecord
    } = data

    const appView = APP_DETAIL_PAGE === this.props.router.pathname
    const showBackFab = !appView && mode === 'Component'
    // const showCMAOutLinks = !appView
    const showOutLinks = !appView

    // const showLotDetails =
    //   property.PropertyUse !== 'Condo' &&
    //   property.PropertyUse !== 'Townhouse' &&
    //   (
    //     isValidNumber(property.slope) ||
    //     isValidNumber(property.lot_size_acres) ||
    //     isValidNumber(property.usable_area) ||
    //     isValidNumber(property.elevation)
    //   )

    const allowedHistory = (authenticated && (currentRole === 'pro' || currentRole === 'expert')) ? history : history?.filter(e => e.TransactionType !== 'Permit')

    const hasLocation = !!property.location
    return (
      <div
        className={cn(
          'relative bg-primary-bg',
          styles['th-property-detail']
        )}
      >
        {this.pageMeta}

        {showBackFab && <BackFab onBack={this.props.onBack} />}

        {mode === 'Page' && !appView && <Header property={property} />}
        <Breadcrumb backToFavorites={this.props.router.query?.onBack === 'toFavorites'} linkEnabled={!appView} property={property} />
        <OverviewInfo property={property} />
        <Actions mode={mode} property={property} showOutLinks={showOutLinks} />
        <Images
          media={media}
          openStreetView={() => this.showSubView('StreetView')}
          property={property}
        />
        <Facts property={property} />
        {mode === 'Page' && (
          <ShowLocationButton property={property} />
        )}
        <BannerAd
          className={bannerStyles['th-banner-section-property-detail']}
          height={180}
          redirectUrl='https://malobo.com/?utm_source=tophap&utm_medium=property_detail&utm_campaign=intro'
          width={600}
        />
        <ClientOnly>
          <StickyOverview
            appView={appView}
            isWindowScroll={mode === 'Page' || viewportWidth <= BREAK_SM}
            property={property}
          >
            <NavBar
              containerId={containerId}
              data={this.props}
              isWindowScroll={mode === 'Page' || viewportWidth <= BREAK_SM}
            />
          </StickyOverview>
        </ClientOnly>
        {/* <div className={cn('overflow-hidden my-3 mx-4 text-sm text-gray-800 ellipses', (mode === 'Page' || viewportWidth >= WIDE_VIEWPORT_WIDTH) ? 'h-[120px]' : 'h-[200px]', (viewportWidth > BREAK_LG) && 'h-[180px]', (viewportWidth > BREAK_XL) && 'h-[100px]')}>{description(property, insightsData, schools)}</div> */}
        <div className={cn('overflow-hidden my-3 mx-4 text-sm text-gray-800 ellipses', (mode === 'Page' || viewportWidth >= WIDE_VIEWPORT_WIDTH) ? 'h-[120px]' : 'h-[200px]', (viewportWidth > BREAK_LG) && 'h-[180px]', (viewportWidth > BREAK_XL) && 'h-[100px]')}>{description(property, insightsData)}</div>
        <Insights property={property} />
        <Valuations
          hasEstimateHistory={estimateHistory?.some(
            zone => zone.estimate.length !== 0
          )}
          hasRentEstimateHistory={rentEstimateHistory?.some(
            zone => zone.estimate.length !== 0
          )}
          property={property}
          // onDetail={this.showSubView}
        />
        {/* // TODO: Need preview server */}
        {hasLocation && (
          <LocationInformation
            // appView={appView}
            property={property}
            onDetail={this.showSubView}
          />
        )}
        {/* {showLotDetails && (
          <LotDetails linkEnabled={!appView} property={property} />
        )} */}
        <MLSDescription property={property} />
        {property.OpenHouses.length ? (
          <OpenHouses openHouses={property.OpenHouses} property={property} />
        ) : null}
        <Tours property={property} />
        {/* <Agents agentInfo={agentInfo} history={history} isGuest={isGuest} property={property} /> */}
        <Agents agentInfo={agentInfo} isGuest={isGuest} property={property} />
        <PropertyDetails
          authenticated={authenticated}
          history={allowedHistory}
          loading={asyncStatus[GetHistory.type] === 'request' || asyncStatus[GetPublicRecord.type] === 'request'}
          property={property}
          publicRecord={publicRecord}
          onDetail={this.showSubView}
        />
        <Permits
          history={allowedHistory}
          property={property}
          withAgentRole
          onDetail={this.showSubView}
        />
        <OwnerInformation
          getOwnerInfo={this.props.getOwnerInfo}
          history={history}
          loading={asyncStatus[GetHistory.type] === 'request'}
          ownerInfo={ownerInfo}
          property={property}
          showOwner={(((appView || authenticated) && currentRole === 'expert') || isReferrerGoogle)}
          onDetail={this.showSubView}
        />
        <HistoryTimeline
          history={allowedHistory}
          propertyDisplayAddress={property.displayAddress}
          onDetail={this.showSubView}
        />
        {hasLocation && <Environment property={property} />}
        {/* {hasLocation && <SchoolInformation property={property} schools={schools} />} */}
        {hasLocation && <SchoolInformation property={property} schools={property.schools} />}
        {/* {hasLocation && (
          <Comparables
            comparables={comparables || []}
            loading={asyncStatus[GetComparables.type] === 'request' || !asyncStatus[GetComparables.type]}
            normalOrWide={this.props.viewportWidth > WIDE_VIEWPORT_WIDTH}
            property={property}
            showCMAOutLinks={showCMAOutLinks}
          />
        )} */}
        {/* Commented out because SageMaker was turned off until we roll out AVM nationwide
        {property.estimates && property.estimates.mlGroup && (
          <Section
            description='The after repair value (ARV) shows estimated property value after changes to condition, age, living area, bedrooms, or bathrooms.'
            id='arv_calculator'
            mode='Card'
            title='ARV Calculator'
          >
            <AuthLock dark event='arv_calculator' requireRole='pro' />
            <div className='py-2 px-4'>
              {predictionBase ? (
                <ProjectedValueCalculator
                  estimates={property.estimates}
                  mode={mode}
                  predictionBase={predictionBase}
                  property={property}
                />
              ) : (
                <div className='block relative p-12 w-full h-[606px] text-center rounded-lg'>
                  {asyncStatus[GetPrediction.type] === 'success' ?
                    (
                      <>
                        <div className='flex justify-center items-center mx-auto mb-1 w-12 h-12 bg-gray-50 rounded-full border shadow-inner'>
                          <SvgCompare className='text-gray-400 xs:w-7 xs:h-7' />
                        </div>
                        <span className='block mx-auto mt-2 w-44 text-sm font-normal text-gray-400'>
                          Calculator not available for this property.
                        </span>
                      </>
                    ) :
                    Array(7).fill('').map((e, i) => (
                      <React.Fragment key={i}>
                        <div className='mb-2 w-11/12 h-4 bg-gray-200 bg-opacity-60 rounded animate-pulse' />
                        <div className='mb-4 w-1/3 h-3 bg-gray-200 bg-opacity-60 rounded animate-pulse' />
                      </React.Fragment>
                    ))}
                </div>
              )}
              <p className='my-3 text-xs text-gray-500'>
                TopHap uses its own automated valuation model (AVM) based on
                available property facts and market data. It is not a formal
                appraisal.
              </p>
            </div>
          </Section>
        )} */}
        {hasLocation && (
          <Neighborhood
            insightsData={insightsData}
            // marketChanges={marketChanges}
            property={property}
          />
        )}
        {/* // TODO: Need nearby stats */}
        {/* {hasLocation && !!property?.zones.length && (
          <NearbyStatistics
            history={history}
            property={property}
          />
        )} */}
        {/* // TODO: Need market history */}
        {hasLocation && !!property.zones.length && (
          <MarketCondition
            loading={!asyncStatus.hasOwnProperty(GetMarketChanges.type) || asyncStatus[GetMarketChanges.type] === 'request'}
            marketChanges={marketChanges}
            property={property}
            onDetail={this.showSubView}
          />
        )}
        {/* {property.mls && mls[property.mls] && (
          <MLS
            className='mb-6'
            date={property.PhotoDate}
            mls={mls[property.mls]}
            showImage={mls[property.mls].logo !== undefined}
          />
        )} */}
        {property.mlsDisclaimer && property.StandardStatus !== 'Sold' && (
          <MLS
            className='mb-6'
            // date={property.PhotoDate}
            disclaimer={property.mlsDisclaimer}
          />
        )}

        {!appView && this.props.sitemap && (
          <Section
            contentClassName='pt-2'
            id='properties_nearby'
            mode='Content'
            title={`Nearby Properties on ${startCase(property.address.StreetName.toLowerCase())}`}
          >
            <Sitemap components={['usa', ...(this.props.sitemapComponents ? this.props.sitemapComponents : [])]} sitemap={this.props.sitemap} />
          </Section>
        )}
        {!appView && <BottomBar className='sticky bottom-0' property={property} />}
        {this.renderSubView}
      </div>
    )
  }
}

export default withRouter(PropertyDetail)
